import styles from './TaskLabelSelector.module.css';
import { ReactNode, useRef } from 'react';
import type { CustomFieldOption } from '@he-novation/config/types/custom-field.types';
import {
    AbsoluteContentButton,
    AbsoluteContentButtonProps
} from '@he-novation/design-system/components/buttons/AbsoluteContentButton/AbsoluteContentButton';
import { DiscText } from '@he-novation/design-system/components/text/DiscText/DiscText';
import Chips from '@he-novation/design-system/components/widgets/Chips/Chips';
import { colorTextFaded } from '@he-novation/design-system/constants/constants.style';
import { DirectionX, DirectionY } from '@he-novation/design-system/utils/getAbsolutePosition';
import cn from 'classnames';
import { TaskLabelChips } from './TaskLabelChips';

type TaskLabelSelectorProps = Omit<AbsoluteContentButtonProps, 'content'> & {
    selectedTaskLabel?: CustomFieldOption;
    defaultLabel?: string;
    onSelect?: (label: CustomFieldOption | null) => void;
    noneLabel: string;
    labels: CustomFieldOption[];
    children?: ReactNode;
};

export function TaskLabelSelector({
    selectedTaskLabel,
    defaultLabel,
    onSelect,
    direction = [DirectionX.LeftInner, DirectionY.Bottom],
    children,
    className,
    labels,
    noneLabel,
    ...props
}: TaskLabelSelectorProps) {
    const ref = useRef<{ setIsVisible: (isVisible: boolean) => void }>();

    return labels.length > 0 ? (
        <AbsoluteContentButton
            direction={direction}
            content={
                <ul className={styles.absoluteContent}>
                    <li
                        key={'none'}
                        onClick={() => {
                            if (!selectedTaskLabel) return;
                            onSelect?.(null);
                            ref.current?.setIsVisible(false);
                        }}
                        className={!selectedTaskLabel ? 'disabled' : undefined}
                    >
                        <DiscText color={colorTextFaded}>{noneLabel}</DiscText>
                    </li>

                    {labels.map((taskLabel) => {
                        const selected = selectedTaskLabel?.uuid === taskLabel.uuid;
                        return (
                            <li
                                key={taskLabel.uuid}
                                onClick={() => {
                                    if (selected) return;
                                    onSelect?.(taskLabel);
                                    ref.current?.setIsVisible(false);
                                }}
                                className={selected ? 'disabled' : undefined}
                            >
                                <DiscText color={taskLabel.color || 'transparent'}>
                                    {taskLabel.label}
                                </DiscText>
                            </li>
                        );
                    })}
                </ul>
            }
            controlRef={ref}
            triggersOnHover={false}
            triggersOnClick={true}
            closeOnClickOutside={true}
            closeOnTriggerClick={true}
            closeOnContentClick={true}
            className={cn(styles.taskLabelSelector, className)}
            {...props}
        >
            {selectedTaskLabel ? (
                <TaskLabelChips taskLabel={selectedTaskLabel} />
            ) : children ? (
                children
            ) : defaultLabel ? (
                <Chips>{defaultLabel}</Chips>
            ) : null}
        </AbsoluteContentButton>
    ) : null;
}
